import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtMXN } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Minus, Pizza, Search, Send, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline panel to add products to an existing order.
 * Used in OrdenDetalle (mesero view) and standalone NuevaOrden.
 */
export default function AgregarItemsPanel({ ordenId, onChanged, pendingItemsCount = 0 }) {
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [activeCat, setActiveCat] = useState("ALL");
  const [q, setQ] = useState("");

  const [variantOpen, setVariantOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentVariantes, setCurrentVariantes] = useState([]);
  const [currentVariantesRapidas, setCurrentVariantesRapidas] = useState([]);
  const [nuevaVarianteTxt, setNuevaVarianteTxt] = useState("");
  const [currentCantidad, setCurrentCantidad] = useState(1);
  const [currentObs, setCurrentObs] = useState("");
  const [sugerencias, setSugerencias] = useState([]);

  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  const [incluirAcomp, setIncluirAcomp] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/productos"), api.get("/categorias")]).then(([p, c]) => {
      setProductos(p.data);
      setCategorias(c.data);
    });
  }, []);

  const filtered = useMemo(() => {
    let list = productos;
    if (activeCat !== "ALL") list = list.filter((p) => p.categoria_id === activeCat);
    if (q.trim()) {
      const term = q.toLowerCase();
      list = list.filter((p) => {
        if (p.nombre.toLowerCase().includes(term)) return true;
        const cat = categorias.find((c) => c.id === p.categoria_id);
        if (cat?.nombre.toLowerCase().includes(term)) return true;
        // buscar en variantes
        for (const g of p.variantes || []) {
          if (g.nombre.toLowerCase().includes(term)) return true;
          for (const o of g.opciones || []) {
            if (o.nombre.toLowerCase().includes(term)) return true;
          }
        }
        return false;
      });
    }
    return list;
  }, [productos, categorias, activeCat, q]);

  const openProducto = (prod) => {
    setCurrentProduct(prod);
    const pre = [];
    (prod.variantes || []).forEach((g) => {
      if (g.obligatoria && !g.multiple && g.opciones[0]) {
        pre.push({ grupo: g.nombre, opcion: g.opciones[0].nombre, precio_extra: g.opciones[0].precio_extra });
      }
    });
    setCurrentVariantes(pre);
    setCurrentVariantesRapidas([]);
    setNuevaVarianteTxt("");
    setCurrentCantidad(1);
    setCurrentObs("");
    setIncluirAcomp(true);
    setVariantOpen(true);
    // load sugerencias
    api.get("/quick-options", { params: { producto_id: prod.id } })
      .then((r) => setSugerencias(r.data))
      .catch(() => setSugerencias([]));
  };

  const toggleSugerencia = (texto) => {
    if (currentVariantesRapidas.includes(texto)) {
      setCurrentVariantesRapidas(currentVariantesRapidas.filter((t) => t !== texto));
    } else {
      setCurrentVariantesRapidas([...currentVariantesRapidas, texto]);
    }
  };

  const setOpcion = (grupo, opcion) => {
    if (grupo.multiple) {
      const exists = currentVariantes.find((v) => v.grupo === grupo.nombre && v.opcion === opcion.nombre);
      if (exists) setCurrentVariantes(currentVariantes.filter((v) => !(v.grupo === grupo.nombre && v.opcion === opcion.nombre)));
      else setCurrentVariantes([...currentVariantes, { grupo: grupo.nombre, opcion: opcion.nombre, precio_extra: opcion.precio_extra }]);
    } else {
      const filtered = currentVariantes.filter((v) => v.grupo !== grupo.nombre);
      setCurrentVariantes([...filtered, { grupo: grupo.nombre, opcion: opcion.nombre, precio_extra: opcion.precio_extra }]);
    }
  };

  const agregarVarianteRapida = () => {
    const t = nuevaVarianteTxt.trim();
    if (!t) return;
    setCurrentVariantesRapidas([...currentVariantesRapidas, t]);
    setNuevaVarianteTxt("");
  };

  const removeVarianteRapida = (i) => setCurrentVariantesRapidas(currentVariantesRapidas.filter((_, idx) => idx !== i));

  const confirmar = async () => {
    const missing = (currentProduct.variantes || []).find((g) => g.obligatoria && !currentVariantes.some((v) => v.grupo === g.nombre));
    if (missing) return toast.error(`Falta seleccionar: ${missing.nombre}`);
    setAdding(true);
    try {
      const payload = {
        items: [{
          producto_id: currentProduct.id,
          cantidad: currentCantidad,
          variantes: currentVariantes,
          variantes_rapidas: currentVariantesRapidas,
          observaciones: currentObs,
          incluir_acompanamiento: currentProduct.acompanamiento_id ? !!incluirAcomp : null,
        }],
      };
      await api.post(`/ordenes/${ordenId}/items`, payload);
      toast.success(`${currentProduct.nombre} agregado`);
      setVariantOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const enviarACocina = async () => {
    setSending(true);
    try {
      const r = await api.post(`/ordenes/${ordenId}/enviar-cocina`);
      toast.success(`${r.data.items_enviados} producto(s) enviado(s) a cocina`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />Agregar productos
          </h3>
          {pendingItemsCount > 0 && (
            <Button
              onClick={enviarACocina}
              disabled={sending}
              className="h-11 font-bold"
              data-testid="enviar-cocina-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Enviar nuevos a cocina ({pendingItemsCount})
            </Button>
          )}
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar producto, categoría o variante…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11"
              data-testid="buscar-producto-input"
            />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant={activeCat === "ALL" ? "default" : "outline"} onClick={() => setActiveCat("ALL")}>Todos</Button>
          {categorias.map((c) => (
            <Button key={c.id} size="sm" variant={activeCat === c.id ? "default" : "outline"} onClick={() => setActiveCat(c.id)}>{c.nombre}</Button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-h-96 overflow-y-auto">
          {filtered.map((p) => {
            // Precio mínimo (primera opción de variante obligatoria 'Tipo y tamaño' o precio_base)
            let precioMin = p.precio_base || 0;
            const tipoT = (p.variantes || []).find((g) => g.obligatoria);
            if (tipoT && tipoT.opciones?.length) {
              const minExtra = Math.min(...tipoT.opciones.map((o) => o.precio_extra || 0));
              precioMin = p.precio_base + minExtra;
            }
            return (
              <Card key={p.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => openProducto(p)} data-testid={`agregar-prod-${p.id}`}>
                <CardContent className="p-3">
                  <div className="aspect-video rounded-lg bg-secondary mb-2 overflow-hidden flex items-center justify-center">
                    {p.imagen_url ? <img src={p.imagen_url.startsWith("http") ? p.imagen_url : `${API}${p.imagen_url}`} alt={p.nombre} className="w-full h-full object-cover" /> : <Pizza className="h-8 w-8 text-muted-foreground" />}
                  </div>
                  <div className="font-bold text-sm leading-tight line-clamp-2" title={p.nombre}>{p.nombre}</div>
                  <div className="flex justify-between mt-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.area}</div>
                    <div className="font-black text-primary text-sm">{precioMin > 0 ? `desde ${fmtMXN(precioMin)}` : ""}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {filtered.length === 0 && <div className="col-span-full text-sm text-muted-foreground text-center py-6">Sin coincidencias</div>}
        </div>
      </CardContent>

      <Dialog open={variantOpen} onOpenChange={setVariantOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{currentProduct?.nombre}</DialogTitle></DialogHeader>
          {currentProduct && (
            <div className="space-y-4">
              {(currentProduct.variantes || []).map((g) => (
                <div key={g.id}>
                  <div className="text-sm font-bold mb-2">{g.nombre}{g.obligatoria && <span className="text-destructive ml-1">*</span>}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {g.opciones.map((o) => {
                      const sel = currentVariantes.find((v) => v.grupo === g.nombre && v.opcion === o.nombre);
                      return (
                        <button key={o.id || o.nombre} type="button" onClick={() => setOpcion(g, o)}
                          className={`px-3 py-2 rounded-lg border-2 text-sm text-left transition-colors ${sel ? "border-primary bg-primary/10 font-bold" : "border-border hover:border-primary/50"}`}>
                          <div>{o.nombre}</div>
                          {o.precio_extra > 0 && <div className="text-xs text-muted-foreground">+{fmtMXN(o.precio_extra)}</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Complemento configurado para el producto */}
              {currentProduct?.acompanamiento_id && currentProduct?.acompanamiento_nombre && (
                <div className="border-t border-border pt-3" data-testid="agregar-acomp-section">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Complemento</Label>
                  <label
                    className={`mt-2 flex items-center justify-between gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${incluirAcomp ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                    data-testid="agregar-acomp-toggle"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={incluirAcomp}
                        onChange={(e) => setIncluirAcomp(e.target.checked)}
                        data-testid="agregar-acomp-checkbox"
                      />
                      <div className="min-w-0">
                        <div className="font-bold text-sm">🍟 {currentProduct.acompanamiento_nombre}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          {currentProduct.acompanamiento_tiene_costo ? "Costo adicional" : "Incluido sin costo"}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm font-black ${currentProduct.acompanamiento_tiene_costo ? "text-primary" : "text-emerald-600 dark:text-emerald-400"}`}>
                      {currentProduct.acompanamiento_tiene_costo
                        ? `+${fmtMXN(currentProduct.acompanamiento_costo || 0)}`
                        : "Gratis"}
                    </div>
                  </label>
                </div>
              )}

              {/* Variantes rápidas (no en catálogo) */}
              <div className="border-t border-border pt-3">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Opciones rápidas sugeridas</Label>
                {sugerencias.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {sugerencias.map((s) => {
                      const sel = currentVariantesRapidas.includes(s.texto);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleSugerencia(s.texto)}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${sel ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
                          data-testid={`sugerencia-${s.texto}`}
                        >
                          {s.texto}
                        </button>
                      );
                    })}
                  </div>
                )}
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mt-3 block">Otra opción (texto libre)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={nuevaVarianteTxt}
                    onChange={(e) => setNuevaVarianteTxt(e.target.value)}
                    placeholder="Ej. Sin cilantro, picante…"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarVarianteRapida(); } }}
                    data-testid="variante-rapida-input"
                  />
                  <Button type="button" variant="outline" onClick={agregarVarianteRapida}>Agregar</Button>
                </div>
                {currentVariantesRapidas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {currentVariantesRapidas.map((t, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 rounded-md text-xs font-semibold">
                        ⚡ {t}
                        <button type="button" onClick={() => removeVarianteRapida(i)}><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-2">⚠ Las opciones nuevas se registran en auditoría y se guardan para futuras órdenes.</div>
              </div>

              <div>
                <Label>Observaciones del producto</Label>
                <Textarea value={currentObs} onChange={(e) => setCurrentObs(e.target.value)} placeholder="Notas internas para cocina" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Cantidad</Label>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" onClick={() => setCurrentCantidad((c) => Math.max(1, c - 1))}><Minus className="h-4 w-4" /></Button>
                  <span className="text-2xl font-black w-10 text-center">{currentCantidad}</span>
                  <Button size="icon" variant="outline" onClick={() => setCurrentCantidad((c) => c + 1)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantOpen(false)}>Cancelar</Button>
            <Button onClick={confirmar} disabled={adding} data-testid="confirmar-agregar-btn">{adding ? "Agregando…" : "Agregar al pedido"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
